// ============================================
// Location Permission Banner
// Handles both HTTPS (GPS) and HTTP (IP fallback)
// ============================================

import React, { useState, useEffect } from 'react';

const LocationPermissionBanner = ({ role = 'user', onLocationGranted }) => {
    const [permissionState, setPermissionState] = useState('checking');
    // const [isSecure, setIsSecure] = useState(true);

    useEffect(() => {
        // Check if site is on HTTPS (required for geolocation)
        const secure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
        // setIsSecure(secure);

        if (!secure) {
            // On HTTP: use IP-based geolocation as fallback
            fetchIPLocation();
            return;
        }

        // On HTTPS: check browser permission state
        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions.query({ name: 'geolocation' }).then((result) => {
                setPermissionState(result.state);
                result.addEventListener('change', () => {
                    setPermissionState(result.state);
                });
            }).catch(() => {
                setPermissionState('prompt');
            });
        } else {
            setPermissionState('prompt');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchIPLocation = async () => {
        try {
            // Free IP geolocation API — no API key needed
            const res = await fetch('https://ipapi.co/json/');
            const data = await res.json();
            if (data.latitude && data.longitude) {
                setPermissionState('ip-granted');
                if (onLocationGranted) {
                    onLocationGranted({
                        lat: data.latitude,
                        lng: data.longitude
                    });
                }
            }
        } catch (err) {
            console.warn('IP geolocation fallback failed:', err);
            // Still hide the banner - don't block the UI
            setPermissionState('ip-granted');
        }
    };

    const requestLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setPermissionState('granted');
                    if (onLocationGranted) {
                        onLocationGranted({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                        });
                    }
                },
                (error) => {
                    console.error('Location permission error:', error);
                    setPermissionState('denied');
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        }
    };

    const isDriver = role === 'driver';

    // Don't show if granted
    // For users, IP fallback is acceptable. For drivers, we MUST have true GPS.
    if (permissionState === 'granted' || permissionState === 'checking' || (!isDriver && permissionState === 'ip-granted')) {
        return null;
    }

    const isDenied = permissionState === 'denied';

    if (isDriver) {
        // Prominent non-blocking inline banner for drivers
        return (
            <div className={`rounded-2xl p-5 sm:p-6 mb-6 flex flex-col sm:flex-row items-center gap-4 shadow-xl border animate-slide-up ${
                isDenied 
                    ? 'bg-rose-50 border-rose-200 shadow-rose-500/10' 
                    : 'bg-indigo-50 border-indigo-200 shadow-indigo-500/10'
            }`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 ${
                    isDenied ? 'bg-rose-100 text-rose-500' : 'bg-indigo-100 text-indigo-500'
                }`}>
                    {isDenied ? (
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    ) : (
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    )}
                </div>
                
                <div className="flex-1 text-center sm:text-left">
                    <h3 className={`font-black text-lg ${isDenied ? 'text-rose-800' : 'text-indigo-900'}`}>
                        {isDenied ? 'Location Access Blocked' : 'Enable Live Tracking'}
                    </h3>
                    <p className={`text-sm mt-1 font-medium ${isDenied ? 'text-rose-600' : 'text-indigo-700'}`}>
                        {isDenied 
                            ? 'You MUST allow location access in your browser settings to receive and execute jobs.'
                            : 'IdaCab Driver requires your GPS location to track active missions.'
                        }
                    </p>
                </div>

                <div className="flex-shrink-0 w-full sm:w-auto mt-2 sm:mt-0">
                    {!isDenied ? (
                        <button
                            onClick={requestLocation}
                            className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-sm uppercase tracking-wide text-white bg-indigo-600 hover:bg-indigo-700 shadow-md transition-all active:scale-95"
                        >
                            Allow Location
                        </button>
                    ) : (
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-sm uppercase tracking-wide text-rose-600 bg-rose-100 hover:bg-rose-200 transition-all active:scale-95"
                        >
                            Refresh Page
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 shadow-lg border animate-slide-up ${
            isDenied 
                ? 'bg-rose-50 border-rose-200' 
                : 'bg-amber-50 border-amber-200'
        }`}>
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 ${
                isDenied ? 'bg-rose-100' : 'bg-amber-100'
            }`}>
                {isDenied ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                )}
            </div>
            <div className="flex-1 text-center sm:text-left">
                <h4 className={`font-black text-sm sm:text-base tracking-tight ${
                    isDenied ? 'text-rose-800' : 'text-slate-900'
                }`}>
                    {isDenied ? 'Location Access Blocked' : 'Enable Location Services'}
                </h4>
                <p className={`text-xs mt-0.5 font-medium ${
                    isDenied ? 'text-rose-600' : 'text-slate-500'
                }`}>
                    {isDenied 
                        ? 'Location was denied. Please enable it in your browser settings.'
                        : 'Allow location access to see nearby drivers and get accurate pickup points on the map.'
                    }
                </p>
            </div>
            {!isDenied && (
                <button
                    onClick={requestLocation}
                    className="px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest text-white shadow-lg transition-all transform active:scale-95 whitespace-nowrap bg-amber-600 hover:bg-amber-700 shadow-amber-600/30"
                >
                    Enable Now
                </button>
            )}
        </div>
    );
};

export default LocationPermissionBanner;
